import React, { useEffect, useState } from "react";
import {
  Airplay,
  ChevronLeft,
  Eye,
  Feather,
  Menu,
  Play,
  Target,
} from "react-feather";
import { Link, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";

import { useToast } from "../Components/toast/ToastProvider";
import Sidebar from "./Sidebar";
import Modal from "../Components/Modal";
import ContractorPaymentForm from "../Components/ContractorPaymentForm";

import {
  getAllContractorPayments,
  getContractorPaymentByFilter,
  saveContractorPayment,
  updateContractorPayment,
  deleteContractorPayment,
} from "../Service/ContractorPaymentService";

import { getAllContractors } from "../Service/ContractorService";
import Footer from "../Components/Footer";

const ContractorPayment = ({ handleLogout }) => {
  const [showSideBar, setShowSideBar] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [date, setDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });

  const [payments, setPayments] = useState([]);
  const [contractors, setContractors] = useState([]);

  const [paymentForm, setPaymentForm] = useState({
    contractor_id: "",
    date: "",
    amount: "",
    remarks: "",
  });

  const toast = useToast();

  const location = useLocation();
  const currentPath = location.pathname;

  useEffect(() => {
    fetchContractors();
  }, []);

  useEffect(() => {
    if (date) {
      fetchPayments(date);
    }
  }, [date]);

  const fetchContractors = async () => {
    try {
      const res = await getAllContractors();
      setContractors(res.data || []);
    } catch (err) {
      toast(
        err.message || "Failed to fetch contractors",
        "ERROR"
      );
    }
  };

  const fetchPayments = async (selectedDate) => {
    try {
      const res = await getContractorPaymentByFilter({
        contractor_id: "",
        from_date: selectedDate,
        to_date: selectedDate,
      });

      setPayments(res.data || []);
    } catch (err) {
      toast(
        err.message || "Failed to fetch contractor payments",
        "ERROR"
      );
    }
  };

  const handleAddClick = () => {
    setPaymentForm({
      contractor_id: "",
      date: date,
      amount: "",
      remarks: "",
    });

    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setPaymentForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !paymentForm.contractor_id ||
      !paymentForm.date ||
      !paymentForm.amount
    ) {
      toast("Please fill all required fields", "ERROR");
      return;
    }

    if (Number(paymentForm.amount) <= 0) {
      toast("Amount must be greater than zero", "ERROR");
      return;
    }

    try {
      if (paymentForm.id) {
        await updateContractorPayment(paymentForm);

        toast(
          "Contractor payment updated successfully",
          "SUCCESS"
        );
      } else {
        await saveContractorPayment(paymentForm);

        toast(
          "Contractor payment added successfully",
          "SUCCESS"
        );
      }

      setShowForm(false);

      await fetchPayments(date);

      handleCancel();
    } catch (err) {
      console.error(err);

      toast(
        err.message || "Failed to save contractor payment",
        "ERROR"
      );
    }
  };

  const handleEdit = (payment) => {
    setPaymentForm({
      id: payment.id,
      contractor_id: payment.contractor_id,
      date: payment.date,
      amount: payment.amount,
      remarks: payment.remarks || "",
    });

    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this payment?"
      )
    ) {
      return;
    }

    try {
      const res = await deleteContractorPayment(id);

      if (res.success === 0 || res.success === false) {
        toast(
          res.message || "Unable to delete payment",
          "ERROR"
        );
        return;
      }

      toast(
        "Contractor payment deleted successfully",
        "SUCCESS"
      );

      await fetchPayments(date);
    } catch (err) {
      toast(
        err.message || "Failed to delete payment",
        "ERROR"
      );
    }
  };

  const handleCancel = () => {
    setPaymentForm({
      contractor_id: "",
      date: date,
      amount: "",
      remarks: "",
    });

    setShowForm(false);
  };

  const getContractorName = (contractorId) => {
    const contractor = contractors.find(
      (item) => Number(item.id) === Number(contractorId)
    );

    return contractor ? contractor.name : "";
  };

  const getContractorContact = (contractorId) => {
    const contractor = contractors.find(
      (item) => Number(item.id) === Number(contractorId)
    );

    return contractor ? contractor.contact_number : "";
  };

  const totalAmount = payments.reduce(
    (total, item) => total + Number(item.amount || 0),
    0
  );

  return (
    <div className="fixed-bottom-bar">
      <div className="osahan-trending">

        {/* Header */}
        <div className="bg-primary border-bottom p-3">
          <div className="d-flex align-items-center justify-content-between">
            <Link to="/" className="text-white font-weight-bold">
              <ChevronLeft /> Back
            </Link>

            <Link className="toggle">
              <span
                className="toggle"
                onClick={() => setShowSideBar(true)}
                style={{ cursor: "pointer" }}
              >
                <Menu size={30} color="white" />
              </span>
            </Link>
          </div>

          <h2 className="font-weight-bold pt-3 m-0 text-white">
            Contractor Payment
          </h2>
        </div>

        {/* Add Button */}
        <div className="d-flex justify-content-end mt-3 px-3">
          <Button
            variant="secondary"
            size="sm"
            style={{
              outline: "none",
              border: "none",
            }}
            onClick={handleAddClick}
          >
            Add
          </Button>
        </div>

        {/* Payment Form Modal */}
        <Modal
          visible={showForm}
          size="md"
          headerText={
            paymentForm.id
              ? "Edit Contractor Payment"
              : "Add Contractor Payment"
          }
          onClose={async () => {
            setShowForm(false);
            await fetchPayments(date);
          }}
        >
          <ContractorPaymentForm
            paymentForm={paymentForm}
            contractors={contractors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            handleCancel={handleCancel}
          />
        </Modal>

        {/* Date Filter */}
        <div className="mt-3 p-3">
          <input
            type="date"
            className="form-control"
            value={date}
            name="date"
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        {/* Total */}
        <div className="px-3">
          <div className="card shadow-sm border-0">
            <div className="card-body p-3 text-center">
              <small className="text-muted">
                Total Payment
              </small>

              <h4 className="text-success fw-bold mt-1 mb-0">
                ₹ {totalAmount.toFixed(2)}
              </h4>
            </div>
          </div>
        </div>

        {/* Payment Cards */}
        <div className="most_popular p-3">
          <div className="row justify-content-center">

            {payments.length === 0 ? (
              <div className="text-center text-muted py-4">
                No contractor payments found
              </div>
            ) : (
              payments.map((payment) => (
                <div
                  className="col-12 d-flex justify-content-center mb-3"
                  key={payment.id}
                >
                  <div
                    className="card shadow-sm bill-card border-0"
                    style={{ width: "100%" }}
                  >
                    <div className="card-body p-3">

                      {/* Contractor */}
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h5
                            className="fw-bold text-muted mb-1"
                            style={{
                              textTransform: "capitalize",
                            }}
                          >
                            {payment.contractor_name ||
                              getContractorName(
                                payment.contractor_id
                              )}
                          </h5>

                          <small className="text-muted">
                            {payment.contact_number ||
                              getContractorContact(
                                payment.contractor_id
                              )}
                          </small>
                        </div>

                        {/* Amount */}
                        <h5 className="text-success fw-bold mb-0">
                          ₹ {Number(payment.amount).toFixed(2)}
                        </h5>
                      </div>

                      <hr className="my-2" />

                      {/* Remarks */}
                      {payment.remarks && (
                        <div className="mb-2">
                          <small className="text-muted">
                            Remarks
                          </small>

                          <div className="fw-semibold">
                            {payment.remarks}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="d-flex justify-content-between gap-2">
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => handleEdit(payment)}
                        >
                          Edit
                        </Button>

                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() =>
                            handleDelete(payment.id)
                          }
                        >
                          Delete
                        </Button>
                      </div>

                    </div>
                  </div>
                </div>
              ))
            )}

          </div>
        </div>

        {/* Sidebar */}
        <Sidebar
          showSideBar={showSideBar}
          setShowSideBar={setShowSideBar}
          onLogout={handleLogout}
        />

        {/* Footer */}
        <Footer />

      </div>
    </div>
  );
};

export default ContractorPayment;